<!-- TODO - gather all the versions from the v2 app and create one final component with css and everything. Also make sure it doesnt get cropped by overflow when added to buttons etc. -->

<template>
  <div class="select-item-box" >
    <slot name="title">
      <div class="title-wrapper">
        <h5 v-if="!hideTitle">{{ title }}</h5>
        <span
          class="clear-all-wrapper"
          @click.stop="clearAll" >
          <!--
          note: having v-if on span above causes click-outside directive to trigger and close this component
          that's why v-if is defined on template tag below
          -->
          <template
            v-if="clearAllDisplayed"
            name="clear-all">
            Clear all
            <SBIcon
              :icon="SBIconList.CLOSE_THIN"
              :color="SBColors.blackUltralight"
              :size="8"
              :pointer="true"/>
          </template>
        </span>
      </div>
    </slot>
    <div
      v-if="allItemsCount > 5 || allItemsList.length > 5 || searchTermValue"
      class="search-input-wrapper">
      <b-form-input
        ref="searchTermInputRef"
        v-model="searchTermValue"
        type="text"
        :placeholder="searchTermPlaceholder"/>
      <SBIcon
        v-if="searchTermValue"
        :icon="SBIconList.CLOSE_THIN"
        :color="SBColors.blackUltralight"
        :hover-icon-color="SBColors.blackPrimary"
        :size="8"
        :hover-size="16"
        :pointer="true"
        @click="clearSearchTerm" />
    </div>
    <ScrollArea
      :bottom-offset="100"
      @bottom="handleBottomScroll">
      <ul
        ref="itemListWrapperRef"
        class="search-results-list">
        <li
          v-for="(item, index) in searchResults"
          :key="index"
          class="flex flex-align-center"
          :class="{selected: selectedId === index}"
          @click="selectItem(item)"
          @mouseover="mouseSelect(index)"
          @mouseout="deselect()" >
          <slot
            :searchTerm="searchTerm"
            :item="item"
            :index="index">
            {{ index }}
          </slot>
        </li>
        <span
          v-if="searchResults.length === 0 && !showLoader"
          class="branch-not-found" >There is no match for '<b>{{ searchTermValue }}</b>'.</span>
        <Spinner
          v-if="showLoader"
          :size="20"
          :thickness="2" />
      </ul>
    </ScrollArea>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import ScrollArea from '@/components/common/ScrollArea';
import Spinner from '@/components/common/Spinner';


/**
 * This is copied from src/components/business/branch/selectItem.vue
 * Components should be refactored and combined into one generic search+select component with slots and props...
 *
 * defines a default slot for rendering an item in the list
 * slot exposes 2 props: index (numerical index) and item (item data)
 * usage example:
<searchable-list
  title="SEARCHABLE LIST"
  :item-list="ARRAY_OF_ITEMS"
  :filter-fn="FUNCTION_THAT_FILTERS_RESULTS">
  <template #default="slotProps">
    Item at index {{ slotProps.index }} has name {{ slotProps.item.name }}
    and we are searching for {{ slotProps.searchTerm }}
  </template>
</searchable-list>
 *
 *
 */

export default {
  name: 'SearchableList',
  components: {
    SBIcon,
    ScrollArea,
    Spinner,
  },
  props: {
    asyncMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    showLoader: {
      type: Boolean,
      required: false,
      default: false,
    },
    title: {
      type: String,
      required: false,
      default: 'Select item',
    },
    searchTermPlaceholder: {
      type: String,
      required: false,
      default: 'Search',
    },
    searchTerm: {
      type: String,
      required: false,
      default: '',
    },
    clearAllDisplayed: {
      type: Boolean,
      required: false,
      default: false,
    },
    itemList: {
      type: Array,
      required: true,
    },
    filterFn: {
      type: Function,
      required: false,
      default: () => {},
    },
    /**
     * Prevents autofocus of the search input field once the component is mounted.
     * This is especially helpfull if the component is used on "slide-in" menus because if the input field gets the focus right away then this section will be in the view initially and this is probably not what we need.
     */
    noFocus: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideTitle: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      searchTermValue: '',
      SBIconList,
      SBColors,
      selectedId: null,
      searchUsed: true, // Set this to false if we decide to enable arrow keys only after the search has been used.
      mouseSelectAllowed: true,
      mouseResetTimeout: null,
      /** Needed because if the search is used and there is no results then the list is 0 and no search is shown. */
      allItemsCount: 0,
    };
  },
  computed: {
    ...mapGetters({
      activeConversation: 'chat/activeConversation',
      currentOrganisationMembers: 'organisation/currentOrganisationMembers',
    }),
    allItemsList() {
      return this.itemList;
    },
    searchResults() {
      if (this.searchTermValue && !this.asyncMode) {
        return this.allItemsList.filter(this.filterFn(this.searchTermValue));
      }
      return this.allItemsList;
      // return this.currentOrganisation.businesses.filter(business => business.name.toLowerCase().includes(this.searchTerm.toLowerCase()) || business.phone.includes(this.searchTerm));
    },
    assignedTo() {
      return this.activeConversation.user_id || '';
    },
  },
  watch: {
    itemList() {
      if (this.itemList.length > this.allItemsCount) {
        this.allItemsCount = this.itemList.length;
      }
    },
  },
  created() {
    this.searchTermValue = this.searchTerm;
  },
  mounted() {
    // CS - to trigger watchers after the initialize. Initialization is best done in created hook
    this.$watch('searchTermValue', () => {
      this.searchUsed = true;
      this.selectedId = 0;
      this.$emit('search-for', this.searchTermValue);
    });
    window.addEventListener('keydown', this.handleKeyDown);
    if (!this.noFocus && this.$refs.searchTermInputRef) {
      this.$refs.searchTermInputRef.focus();
    }

    if (this.itemList.length > this.allItemsCount) {
      this.allItemsCount = this.itemList.length;
    }

    this.$nextTick(() => {
      if (this.$refs.searchTermInputRef) {
        this.$refs.searchTermInputRef.focus();
      }
    });
  },
  destroyed() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    clearSearchTerm() {
      setTimeout(() => {
        // DO NOT CHANGE THIS
        // For some reason, if this is not wrapped with setTimeout it triggers v-click-outside directive !?
        this.searchTermValue = '';
      }, 0);
    },
    focusSearchTermInput() {
      this.$refs.searchTermInputRef.focus();
    },
    selectItem(item) {
      this.$emit('item-selected', item);
    },
    clearAll() {
      this.$emit('clear-all');
    },
    handleBottomScroll() {
      this.$emit('load-more-options');
    },
    mouseSelect(itemId) {
      if (this.mouseSelectAllowed) {
        this.select(itemId);
      }
    },
    select(itemId) {
      this.selectedId = itemId;
      this.scrollIntoViewIfNeeded(this.$refs.itemListWrapperRef.children[itemId]);
    },
    deselect() {
      if (this.mouseSelectAllowed) {
        this.selectedId = null;
      }
    },
    scrollIntoViewIfNeeded(target) {
      const rectParent = target.parentElement.getBoundingClientRect();
      const rect = target.getBoundingClientRect();
      this.$nextTick(() => {
        const pseudoElementStyle = getComputedStyle(target.parentElement);
        const borderTopWidth = Number.parseInt(pseudoElementStyle.borderTopWidth.replace('px', ''), 10);
        const topPadding = Number.parseInt(pseudoElementStyle.paddingTop.replace('px', ''), 10);
        const parentTop = rectParent.top + borderTopWidth + topPadding;
        const parentBottom = rectParent.bottom + borderTopWidth + topPadding;
        if (rect.top < parentTop) {
          target.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' });
        } else if (rect.bottom > parentBottom) {
          target.scrollIntoView({ block: 'end', inline: 'nearest', behavior: 'smooth' });
        }
      });
    },
    /**
     * Sets flag to not react on mouse events on the li items. If this is off and mouse is above a li item and we initiate changes with the arrow keys
     * then at some point the list will scroll and the mouse event will be trigered which will cause unwanted result.
     */
    preventMouseAction() {
      this.mouseSelectAllowed = false;
      clearTimeout(this.mouseResetTimeout);
      this.mouseResetTimeout = setTimeout(() => {
        this.mouseSelectAllowed = true;
      }, 1000);
    },
    handleKeyDown(event) {
      switch (event.keyCode) {
        case 38: // UP
          this.preventMouseAction();
          if (this.searchUsed && this.selectedId > 0) {
            this.select(this.selectedId - 1);
          }
          event.preventDefault();
          break;
        case 40: // DOWN
          this.preventMouseAction();
          if (this.searchUsed && this.selectedId === null) {
            this.select(0);
          } else if (this.searchUsed && this.selectedId < this.searchResults.length - 1) {
            this.select(this.selectedId + 1);
          }
          event.preventDefault();
          break;
        case 13: // ENTER
          if (this.selectedId >= 0) {
            this.selectItem(this.searchResults[this.selectedId]);
          }
          break;
        default:
          break;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.select-item-box {
  width: 297px;
  font-size: 14px;

  .title-wrapper {
    position: relative;

    h5 {
      text-align: center;
      font-weight: 500;
      margin: 15px 16px 0;
    }

    .clear-all-wrapper::v-deep {
      position: absolute;
      top: 50%;
      right: 15px;
      // height: 20px;
      margin-top: -10px;

      color: $blackUltralight;
      font-weight: 500;

      white-space: nowrap;
      display: flex;
      align-items: center;
      cursor: pointer;

      div.sb-icon {
        margin-left: 7px;
      }

      &:hover {
        color: $blackPrimary;

        // hacking hover state with v-deep to change icon color when this wrapper is hovered
        span.icon {
          //--color: $blackPrimary !important;
          //--hover-icon-color: $blackPrimary !important;
          // for some reason using $blackPrimary does not work
          --color: #141c20 !important;
          --hover-icon-color: #141c20 !important;
        }
      }
    }
  }

  .search-input-wrapper {
    position: relative;
    // border-bottom: solid 1px #e0e7e9;

    input {
      position: relative;
      padding: 5px 26px 5px 8px;
      margin: 15px 16px 0;
      width: calc(100% - 30px);
      border-radius: 5px;
      box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1) !important;
      border: solid 1px #cfd8dc !important;
      background-color: #ffffff !important;
      height: 26px !important;

      &:focus {
        box-shadow: 0 0 0 3px #bfdeff !important;
      }
    }

    .sb-icon {
      position: absolute;
      top: 5px;
      right: 20px;
    }
  }

  .scroll-area {
    margin-top: 15px;
    padding: 7px 0;
    border-radius: 0 0 5px 5px;
    border-top: solid 1px #e0e7e9;
    max-height: 200px;
  }

  .search-results-list {
    margin: 0;

    li.selected {
      background-color: $hover;
      cursor: pointer;
    }

    .branch-not-found {
      display: block;
      padding: 0 16px;
    }
  }
}
</style>
